/**
 * Client-side auth helpers.
 *
 * Thin wrappers over `fetch('/api/...')`. Better Auth's session cookie is
 * `HttpOnly` so we cannot read it directly — we ask the server via `/api/me`.
 *
 * The sign-in initiation calls hit Better Auth's REST endpoints under
 * `/api/auth/*` directly. We POST JSON bodies; the responses are either a
 * `{ redirect, url }` (OAuth) or a status-only ack (magic-link).
 */

export interface Operator {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string;
  image?: string | null;
}

export class AuthError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.message) msg = j.message;
      else if (j?.error) msg = j.error;
    } catch {
      /* ignore */
    }
    throw new AuthError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Returns the current operator + active provider id + the operator's role
 * for that provider, or `null` if the session cookie is missing or invalid.
 * Network errors propagate.
 *
 * `currentProviderId` is `null` when the signed-in operator has zero
 * memberships — the SPA uses that to route to `/welcome`. `role` is `null`
 * in the same case.
 */
export interface Session {
  user: Operator;
  currentProviderId: string | null;
  role: "owner" | "admin" | "viewer" | null;
}

export async function getSession(): Promise<Session | null> {
  const res = await fetch("/api/me", { credentials: "same-origin" });
  if (res.status === 401) return null;
  if (!res.ok) {
    throw new AuthError(res.status, `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as Session;
}

/**
 * Initiates a social-provider sign-in. Better Auth responds with the OAuth
 * `url` to redirect the browser to. The callback round-trip lands back here
 * at `/api/auth/callback/{provider}`, which sets the session cookie and
 * 302s to `callbackURL` (we pass `/`).
 */
export async function signInWithProvider(
  provider: "google" | "github",
  callbackURL = "/",
): Promise<void> {
  const data = await postJson<{ url?: string; redirect?: boolean }>(
    "/api/auth/sign-in/social",
    {
      provider,
      callbackURL,
    },
  );
  if (data.url) {
    window.location.href = data.url;
    return;
  }
  // Defensive fallback — Better Auth always returns a url for OAuth flows.
  throw new AuthError(500, "OAuth sign-in returned no redirect URL");
}

/**
 * Sends a magic-link email. Resolves on accepted send; the user clicks the
 * link from their inbox to actually create a session.
 */
export async function sendMagicLink(email: string, callbackURL = "/"): Promise<void> {
  await postJson("/api/auth/sign-in/magic-link", {
    email,
    callbackURL,
  });
}

/**
 * Clears the session cookie server-side. Caller is responsible for
 * navigating to `/login` after — we don't redirect here so callers can
 * choose between in-app navigate vs full reload.
 */
export async function signOut(): Promise<void> {
  await postJson("/api/auth/sign-out", {});
}
